import _ from 'lodash';

import * as CanvasUtils from './canvasUtils';
import * as api from '../../../api';
import * as logger from '../../../logger';

import { pdfjs } from 'react-pdf';
import { Direction, Side } from '../../../utils/enum';
import { MERGE_PICTURE_DEFAULT_PADDING } from './canvasUtils';
import { PictureState } from '../UploadCardContainer/types';

import styles from './index.module.css';

const pdfjsWorker = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export enum Labels {
  imageUploadFront = 'Front of',
  imageUploadBack = 'Back of',
}

export interface FileUpload {
  dataUri: string;
  rawExtension: string;
  blob: Blob;
  side: Side;
}

export function getUploadClass(loaded: number, total: number): string {
  const p = Math.round((loaded / total) * 100);

  if (p >= 96) return styles.Progress96;
  if (p >= 87) return styles.Progress87;
  if (p >= 72) return styles.Progress72;
  if (p >= 66) return styles.Progress66;
  if (p >= 52) return styles.Progress52;
  if (p >= 45) return styles.Progress45;
  if (p >= 33) return styles.Progress33;
  if (p >= 26) return styles.Progress26;
  if (p >= 19) return styles.Progress19;
  if (p >= 7) return styles.Progress7;
  return styles.Progress0;
}

export function dataURItoBlob(dataUri: string): Blob {
  var byteString = atob(dataUri.split(',')[1]);
  var mimeString = dataUri.split(',')[0].split(':')[1].split(';')[0];
  var ab = new ArrayBuffer(byteString.length);
  var ia = new Uint8Array(ab);
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}

export function downloadDataUri(dataUri: string, fileName = 'Image.jpeg') {
  const a = document.createElement('a');
  a.href = dataUri;
  a.download = fileName;
  a.click();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise(function (resolve, reject) {
    const image = Object.assign(document.createElement('img'));

    function cleanup() {
      image.onload = null;
      image.onerror = null;
    }

    image.onload = function () {
      cleanup();
      resolve(image);
    };
    image.onerror = function () {
      cleanup();
      reject(new Error(`Failed to load the image "${src}"`));
    };

    image.src = src;
  });
}

export async function mergePictures({
  frontDataUri,
  backDataUri,
  direction = Direction.Horizontal,
  resize = true,
  padding = MERGE_PICTURE_DEFAULT_PADDING,
}: {
  frontDataUri?: string;
  backDataUri?: string;
  direction?: Direction;
  resize?: boolean;
  padding?: number;
}): Promise<string> {
  if (!frontDataUri || !backDataUri) {
    throw new Error('Not all data uri are provided');
  }
  const imageObj1 = await loadImage(frontDataUri);
  const imageObj2 = await loadImage(backDataUri);
  const canvas = CanvasUtils.paintCombinedImageOnCanvas({
    imageObj1,
    imageObj2,
    direction,
    resize,
    padding,
  });
  const dataUrl = canvas.toDataURL('image/jpeg');
  if (dataUrl && dataUrl.length) {
    return dataUrl;
  } else {
    const errorMessage =
      '[UploadDocumentPage][mergePictures] could not get data url from canvas, most likely browser limitation.';
    throw new Error(errorMessage);
  }
}

export async function getDataUriFromFile(file: Blob): Promise<string> {
  return new Promise<string>((resolve) => {
    var reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        resolve(reader.result.toString());
      }
    };
    reader.readAsDataURL(file);
  });
}

export async function sendToOcr(
  uploadId: string,
  side: Side,
  payor?: string,
): Promise<api.InsuranceCardOcrResult.InsuranceCardOcrResult | undefined> {
  try {
    return await api.client.PatientUpload.ocrInsuranceCard(
      uploadId,
      side,
      payor,
    );
  } catch (e) {
    console.warn(`api.client.PatientUpload.ocrInsuranceCard ${e}`);
  }
}

export async function uploadImage<T = any>({
  encounterId,
  imageBlob,
  onUploaded,
  documentType,
  options = {},
  rethrow,
}: {
  imageBlob: Blob;
  encounterId: string;
  onUploaded: (uploadId: string) => Promise<T>;
  patientInsuranceInformationId?: string;
  documentType: api.PatientUpload.DocumentType;
  options?: {
    insuranceProviderName?: string;
    primary?: boolean;
    patientInsuranceInformationId?: string;
  };
  onError?: () => void;
  rethrow?: boolean;
}): Promise<T | undefined> {
  try {
    const { id, url } = await api.client.PatientUpload.getSignedUploadURL(
      encounterId,
      documentType,
      options,
    );
    if (!url) return;
    const status = await api.client.PatientUpload.upload(
      imageBlob,
      url,
      () => {},
    );
    if (status !== 200) {
      throw new Error(
        `received non-200 status when uploading image: ${status}`,
      );
    }

    await api.client.PatientUpload.validateUploadedDocuments(id);
    return await onUploaded(id);
  } catch (e) {
    logger.error(
      e,
      `[UploadDocumentPage][uploadImage] size: ${imageBlob.size} bytes`,
    );
    if (rethrow) throw e;
  }
}

export async function convertPdfToImage(pdf: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = async () => {
      if (fileReader.result && typeof fileReader !== 'string') {
        try {
          const pdf = await pdfjs.getDocument(fileReader.result as Float64Array)
            .promise;
          const firstPage = await pdf.getPage(1);
          const viewport = firstPage.getViewport({ scale: 1.5 });

          // Prepare canvas using PDF page dimensions
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d') as CanvasRenderingContext2D;
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          // Render PDF page into canvas context
          await firstPage.render({
            canvasContext: context,
            viewport,
          }).promise;
          canvas.toBlob((blob) => {
            canvas.remove();
            if (blob) {
              resolve(blob);
            }
            reject('Could not convert to blob');
          });
        } catch (e) {
          reject(e);
        }
      } else {
        reject('Could not load PDF file');
      }
    };
    fileReader.onerror = (e) => {
      reject(e);
    };
    fileReader.readAsArrayBuffer(pdf);
  });
}

export const processUpload = async (
  mergedBlob: Blob,
  url: string,
  onUploadProgress: (event: api.AxiosProgressEvent) => void,
  onError: (errMsg: string) => void,
): Promise<number> => {
  let status = 0;
  try {
    status = await api.client.PatientUpload.upload(
      mergedBlob,
      url,
      onUploadProgress,
    );
  } catch (e) {
    onError(
      'Failed to upload, please verify your internet connection and try again.',
    );
  }
  return status;
};

export const uploadMergedPictures = async (args: {
  frontPicture: Blob;
  backPicture: Blob;
  url: string;
  id: string;
  encounter: api.Encounter.Encounter;
  onUploadProgress: (event: api.AxiosProgressEvent) => void;
  onError: (errMsg: string) => void;
}): Promise<void> => {
  const {
    frontPicture,
    backPicture,
    url,
    id,
    encounter,
    onUploadProgress,
    onError,
  } = args;
  try {
    const merged = await mergePictures({
      frontDataUri: await getDataUriFromFile(frontPicture),
      backDataUri: await getDataUriFromFile(backPicture),
    });

    if (url) {
      const mergedBlob = dataURItoBlob(merged);
      const status = await processUpload(
        mergedBlob,
        url,
        onUploadProgress,
        onError,
      );

      if (status === 200) {
        await api.client.PatientUpload.validateUploadedDocuments(id);
      } else {
        logger.error(
          new Error(
            `Upload for encounter ${encounter.id} to ${url} failed with status ${status}`,
          ),
        );
      }
    }
  } catch (e) {
    throw new Error('Unknown error');
  }
};

export const getLatestUpload = async (args: {
  documentType: api.PatientUpload.DocumentType;
  encounterId: string;
  setUpload: React.Dispatch<
    React.SetStateAction<api.PatientUpload.PatientUpload | null>
  >;
  onComplete: () => void;
}): Promise<api.PatientUpload.PatientUpload | null> => {
  const { documentType, encounterId, setUpload, onComplete } = args;
  const latestDocuments =
    await api.client.PatientUpload.findAllWithDocumentTypeByEncounterId(
      encounterId,
      documentType,
    );
  if (!latestDocuments || !latestDocuments.length) return null;

  const latestDocument = _.sortBy(latestDocuments, 'updated')[
    latestDocuments.length - 1
  ];
  setUpload(latestDocument);
  const { status, dataURL } = await api.client.PatientUpload.downloadImageFile(
    latestDocument.id,
  );
  if (status === 200 && dataURL) {
    latestDocument.gsPath = dataURL;
    onComplete();
  }
  return latestDocument;
};

export const uploadDocument = async (args: {
  pictures: PictureState;
  documentType: api.PatientUpload.DocumentType;
  encounterId: string;
  errorMessage: string;
  setDocument: React.Dispatch<
    React.SetStateAction<api.PatientUpload.PatientUpload | null>
  >;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  onComplete: () => void;
}): Promise<void> => {
  const {
    pictures,
    documentType,
    encounterId,
    errorMessage,
    setDocument,
    setErrorMessage,
    onComplete,
  } = args;
  if (pictures[Side.Front] && pictures[Side.Back]) {
    try {
      const merged = await mergePictures({
        frontDataUri: await getDataUriFromFile(pictures[Side.Front]),
        backDataUri: await getDataUriFromFile(pictures[Side.Back]),
      });
      const signedUploadUrl = await api.client.PatientUpload.getSignedUploadURL(
        encounterId,
        documentType,
      );
      if (signedUploadUrl.url) {
        const mergedBlob = dataURItoBlob(merged);
        const uploadStatus = await api.client.PatientUpload.upload(
          mergedBlob,
          signedUploadUrl.url,
          () => {},
        );
        if (uploadStatus === 200) {
          await api.client.PatientUpload.validateUploadedDocuments(
            signedUploadUrl.id,
          );
          await getLatestUpload({
            documentType: documentType,
            encounterId: encounterId,
            setUpload: setDocument,
            onComplete,
          });
        }
      }
    } catch (e) {
      setErrorMessage(errorMessage);
    }
  }
  await getLatestUpload({
    documentType: documentType,
    encounterId: encounterId,
    setUpload: setDocument,
    onComplete: onComplete,
  });
};
